import { useState, useCallback, useMemo, useEffect } from 'react';
import { PageLayout } from '@/components/layout/PageLayout';
import { ReactFlow, Background, Controls, MiniMap, applyNodeChanges } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { SupplyChainNode } from '@/components/supply-chain/SupplyChainNode';
import { FlowEdge } from '@/components/supply-chain/FlowEdge';
import { MetricWidget } from '@/components/supply-chain/MetricWidget';
import { WidgetEdge } from '@/components/supply-chain/WidgetEdge';
import { AddWidgetDialog } from '@/components/supply-chain/AddWidgetDialog';
import { METRIC_OPTIONS, getNestedValue } from '@/components/supply-chain/metricOptions';
import { NodeDetailPanel } from '@/components/supply-chain/NodeDetailPanel';
import { OeeOverlay } from '@/components/supply-chain/OeeOverlay';
import { FlowLegend } from '@/components/supply-chain/FlowLegend';
import { SilosPanel } from '@/components/supply-chain/SilosPanel';
import { CrusherTariffBadge } from '@/components/supply-chain/CrusherTariffBadge';
import { useSimulation } from '@/hooks/useSimulation';
import { Mountain, Settings2, Filter, Package, Container, Truck, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { t } from '@/i18n';

const iconMap = { Mountain, Settings2, Filter, Package, Container, Truck };

const nodeTypes = { supplyChainNode: SupplyChainNode, metricWidget: MetricWidget };
const edgeTypes = { flowEdge: FlowEdge, widgetEdge: WidgetEdge };

export default function SupplyChain() {
  const navigate = useNavigate();
  const { nodes: simNodes, edges: simEdges, trucks, getOrderById } = useSimulation();
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const [addWidgetOpen, setAddWidgetOpen] = useState(false);

  // Widget handlers
  const handleAddWidget = useCallback(({ nodeId, metric }) => {
    const sourceNode = simNodes.find(n => n.id === nodeId);
    if (!sourceNode) return;
    const id = `widget-${Date.now()}`;
    const metricDef = METRIC_OPTIONS.find(m => m.key === metric);
    // Offset position to avoid overlap with existing widgets on the same node
    setWidgets(prev => {
      const sameNodeWidgets = prev.filter(w => w.nodeId === nodeId);
      const offsetX = sameNodeWidgets.length * 170;
      return [...prev, {
        id,
        nodeId,
        metric,
        position: { x: sourceNode.position.x + offsetX, y: sourceNode.position.y - 120 },
      }];
    });
    toast.success(`${t('supplyChain.widgetAdded')}: ${metricDef ? t(metricDef.labelKey) : metric}`);
  }, [simNodes]);

  const handleRemoveWidget = useCallback((widgetId) => {
    setWidgets(prev => prev.filter(w => w.id !== widgetId));
    toast(t('supplyChain.widgetRemoved'));
  }, []);

  // Build base nodes from sim data + widget definitions
  const baseNodes = useMemo(() => {
    const supplyChainNodes = simNodes.map(node => ({
      id: node.id,
      type: 'supplyChainNode',
      position: node.position,
      draggable: false,
      data: {
        ...node,
        label: t(`supplyChain.node.${node.type}`),
        bottleneckLabel: t('supplyChain.bottleneck'),
        icon: iconMap[node.icon] || Package,
        capacity: node.utilization,
        metrics: { capacity: node.capacity.max, currentOutput: node.capacity.current },
        selected: node.id === selectedNodeId,
      },
    }));

    const widgetNodes = widgets.map(w => {
      const sourceNode = simNodes.find(n => n.id === w.nodeId);
      const metricDef = METRIC_OPTIONS.find(m => m.key === w.metric);
      const rawValue = sourceNode ? getNestedValue(sourceNode, w.metric) : undefined;
      return {
        id: w.id,
        type: 'metricWidget',
        position: w.position,
        draggable: true,
        data: {
          nodeLabel: sourceNode ? t(`supplyChain.node.${sourceNode.type}`) : w.nodeId,
          label: metricDef ? t(metricDef.labelKey) : w.metric,
          value: metricDef?.format(rawValue) ?? rawValue,
          onRemove: () => handleRemoveWidget(w.id),
        },
      };
    });

    return [...supplyChainNodes, ...widgetNodes];
  }, [simNodes, widgets, handleRemoveWidget, selectedNodeId]);

  // Local ReactFlow node state — keeps measured dimensions intact across re-renders
  const [rfNodes, setRfNodes] = useState([]);

  useEffect(() => {
    setRfNodes(prev => {
      const prevMap = new Map(prev.map(n => [n.id, n]));
      return baseNodes.map(node => {
        const existing = prevMap.get(node.id);
        // Preserve measured dimensions and internals from ReactFlow
        return existing
          ? { ...node, measured: existing.measured, width: existing.width, height: existing.height }
          : node;
      });
    });
  }, [baseNodes]);

  const onNodesChange = useCallback((changes) => {
    // Apply ALL changes (dimensions, selection, position) to rfNodes
    setRfNodes(prev => applyNodeChanges(changes, prev));

    // Persist widget final positions back to widget state when drag ends
    for (const c of changes) {
      if (c.type === 'position' && c.id?.startsWith('widget-') && !c.dragging && c.position) {
        setWidgets(prev => prev.map(w =>
          w.id === c.id ? { ...w, position: c.position } : w
        ));
      }
    }
  }, []);

  const allEdges = useMemo(() => {
    const flowEdges = simEdges.map(edge => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      type: 'flowEdge',
      data: { flowRate: edge.flowRate, material: edge.material, status: edge.status },
      style: { stroke: 'transparent' },
    }));

    const widgetEdges = widgets.map(w => ({
      id: `edge-${w.id}`,
      source: w.nodeId,
      target: w.id,
      type: 'widgetEdge',
    }));

    return [...flowEdges, ...widgetEdges];
  }, [simEdges, widgets]);

  const selectedNode = useMemo(() => {
    if (!selectedNodeId) return null;
    const simNode = simNodes.find(n => n.id === selectedNodeId);
    if (!simNode) return null;
    return {
      ...simNode,
      data: {
        ...simNode,
        label: t(`supplyChain.node.${simNode.type}`),
        icon: iconMap[simNode.icon] || Package,
      },
    };
  }, [selectedNodeId, simNodes]);

  // Get linked orders and trucks for selected node
  const linkedOrders = useMemo(() => {
    if (!selectedNode) return [];
    return (selectedNode.linkedOrders || []).map(getOrderById).filter(Boolean);
  }, [selectedNode, getOrderById]);

  const linkedTrucks = useMemo(() => {
    if (!selectedNode) return [];
    // Find trucks whose orders are linked to this node
    const orderIds = selectedNode.linkedOrders || [];
    return trucks.filter(tr => orderIds.includes(tr.orderId) && !['departed'].includes(tr.status));
  }, [selectedNode, trucks]);

  const onNodeClick = useCallback((_event, node) => {
    setSelectedNodeId(node.id);
  }, []);

  // Calculate average OEE with breakdown
  const avgOee = useMemo(() => {
    const sum = simNodes.reduce((acc, n) => acc + (n.oee?.overall || 0), 0);
    return Math.round((sum / simNodes.length) * 10) / 10;
  }, [simNodes]);

  const oeeBreakdown = useMemo(() => {
    const len = simNodes.length || 1;
    return {
      availability: Math.round(simNodes.reduce((s, n) => s + (n.oee?.availability || 0), 0) / len * 10) / 10,
      performance: Math.round(simNodes.reduce((s, n) => s + (n.oee?.performance || 0), 0) / len * 10) / 10,
      quality: Math.round(simNodes.reduce((s, n) => s + (n.oee?.quality || 0), 0) / len * 10) / 10,
    };
  }, [simNodes]);

  const totalThroughput = useMemo(() =>
    Math.round(simNodes.reduce((s, n) => s + (n.capacity?.current || 0), 0)),
  [simNodes]);

  const activeAlertCount = useMemo(() =>
    simNodes.reduce((s, n) => s + (n.alerts?.length || 0), 0),
  [simNodes]);

  return (
    <PageLayout title={t('supplyChain.title')} concept>
      <div className="h-[calc(100vh-8rem)] rounded-2xl border border-border overflow-hidden relative shadow-elevated">
        <ReactFlow
          nodes={rfNodes}
          edges={allEdges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodeClick={onNodeClick}
          onNodesChange={onNodesChange}
          onPaneClick={() => setSelectedNodeId(null)}
          fitView
          className="bg-gradient-to-br from-[hsl(214,17%,97%)] to-[hsl(214,17%,94%)]"
          proOptions={{ hideAttribution: true }}
          minZoom={0.5}
          maxZoom={1.5}
        >
          <Background color="hsl(var(--primary))" gap={24} size={1} variant="dots" />
          <Controls className="!bg-white/80 !backdrop-blur-sm !border-border !rounded-xl !shadow-elevated" />
          <MiniMap
            nodeColor={(node) => {
              if (node.type === 'metricWidget') return 'transparent';
              const s = node.data?.status;
              return s === 'warning' ? '#eab308' : s === 'error' ? '#ef4444' : '#22c55e';
            }}
            nodeStrokeWidth={1}
            nodeStrokeColor={(node) => node.type === 'metricWidget' ? 'transparent' : 'rgba(255,255,255,0.8)'}
            nodeBorderRadius={12}
            maskColor="hsl(var(--chart-1) / 0.08)"
            maskStrokeColor="hsl(var(--chart-1))"
            maskStrokeWidth={1}
            style={{ width: 160, height: 100 }}
            className="!bg-white/90 !backdrop-blur-md !border-border !rounded-xl !shadow-elevated"
          />
        </ReactFlow>

        {/* Overlays */}
        <OeeOverlay oee={avgOee} breakdown={oeeBreakdown} totalThroughput={totalThroughput} alertCount={activeAlertCount} />
        <FlowLegend nodes={simNodes} edges={simEdges} />

        {/* Crusher tariff + cost/t (P1) — placed below OeeOverlay to avoid top-left overlap */}
        <div className="absolute top-[15rem] left-3 z-10 w-52">
          <CrusherTariffBadge />
        </div>

        {/* Silos overview (P0.3) */}
        <div className="absolute bottom-3 right-3 z-10 w-72 max-h-[60vh] overflow-y-auto">
          <SilosPanel />
        </div>

        {/* Add Widget button */}
        <Button
          size="sm"
          variant="outline"
          onClick={() => setAddWidgetOpen(true)}
          className="absolute top-3 right-3 z-10 bg-white/80 backdrop-blur-sm border-border shadow-elevated hover:bg-white/95"
        >
          <Plus className="h-4 w-4 mr-1.5" />
          {t('supplyChain.addWidget')}
        </Button>

        <AddWidgetDialog
          open={addWidgetOpen}
          onOpenChange={setAddWidgetOpen}
          nodes={simNodes}
          onAdd={handleAddWidget}
        />

        {/* Detail Panel */}
        <NodeDetailPanel
          node={selectedNode ? { data: selectedNode.data || selectedNode } : null}
          onClose={() => setSelectedNodeId(null)}
          linkedOrders={linkedOrders}
          linkedTrucks={linkedTrucks}
          onNavigateToOrder={(id) => navigate(`/objednavky?highlight=${id}`)}
          onNavigateToTruck={(id) => navigate(`/kamiony?highlight=${id}`)}
        />
      </div>
    </PageLayout>
  );
}
